import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { BalanceTrendPoint, TimePeriod } from '@/types/account-drilldown';

interface BalanceTrendChartProps {
  data: BalanceTrendPoint[];
  currency: string;
  timePeriod: TimePeriod;
  isLoading: boolean;
}

/**
 * Format date for display based on time period
 */
function formatDate(dateStr: string, timePeriod: TimePeriod): string {
  const date = new Date(dateStr);
  
  if (timePeriod === 'last7days' || timePeriod === 'last30days') {
    // Show month/day for short periods
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (timePeriod === 'last90days') {
    // Show month/day for medium periods
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    // Show month/year for long periods
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
}

/**
 * Format currency value for display
 */
function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Custom tooltip component for the chart
 */
function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;
  const date = new Date(data.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{date}</p>
      <p className="text-lg font-semibold">
        {formatCurrency(data.balance, currency)}
      </p>
    </div>
  );
}

/**
 * Balance Trend Chart Component
 * Displays account balance over time using a line chart
 */
export function BalanceTrendChart({ data, currency, timePeriod, isLoading }: BalanceTrendChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Balance Trend</CardTitle>
          <CardDescription>Loading balance history...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-end justify-between gap-2 px-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="w-full bg-muted animate-pulse rounded-t"
                style={{ height: `${Math.random() * 60 + 40}%` }}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Balance Trend</CardTitle>
          <CardDescription>No balance data available</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No data to display</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Balance Trend</CardTitle>
        <CardDescription>
          Balance history in {currency}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div 
          className="w-full overflow-x-auto"
          role="img"
          aria-label={`Balance trend chart showing ${data.length} data points in ${currency}`}
        >
          <div className="min-w-[500px]">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={data}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                tabIndex={0}
                aria-label="Balance trend line chart"
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDate(value, timePeriod)}
                  className="text-xs"
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value, currency)}
                  className="text-xs"
                />
                <Tooltip content={<CustomTooltip currency={currency} />} />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Screen reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>Balance trend data</caption>
            <thead>
              <tr>
                <th>Date</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.map((point, index) => (
                <tr key={index}>
                  <td>{new Date(point.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</td>
                  <td>{formatCurrency(point.balance, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
